import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Check, X, Loader2, CheckCircle2, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import SignaturePad from "@/components/crm/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;
const USD2 = (n) => new Intl.NumberFormat("es-SV", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(n || 0);
const DATE = (iso) => iso ? new Date(iso).toLocaleDateString("es-SV", { day: "2-digit", month: "long", year: "numeric" }) : "—";

export default function PublicQuote() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [name, setName] = useState("");
  const [sig, setSig] = useState("");
  const [reason, setReason] = useState("");
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/quotes/${token}`);
      setData(r.data);
      setName(r.data.customer?.contactName || "");
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos cargar la cotización.");
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    if (!name.trim() || !sig) { toast.error("Nombre y firma son obligatorios"); return; }
    try {
      setActing(true);
      await axios.post(`${API}/quotes/${token}/accept`, { acceptorName: name.trim(), signature: sig });
      toast.success("¡Cotización aceptada!");
      setAcceptOpen(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al aceptar"); }
    finally { setActing(false); }
  };

  const decline = async () => {
    try {
      setActing(true);
      await axios.post(`${API}/quotes/${token}/decline`, { reason });
      toast.info("Cotización rechazada");
      setDeclineOpen(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al rechazar"); }
    finally { setActing(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>
  );
  if (error) return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <div className="font-display text-4xl">Enlace inválido</div>
        <p className="mt-3 text-sm text-muted-foreground">{error}</p>
      </div>
    </div>
  );

  const q = data.quote;
  const org = data.org;
  const closed = q.status === "ACCEPTED" || q.status === "DECLINED";

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Hero */}
      <div className="mesh-soft border-b border-border" data-testid="public-quote-hero">
        <div className="mx-auto max-w-4xl px-6 py-12 md:px-10 md:py-16">
          <div className="font-display text-5xl leading-none tracking-tight md:text-6xl">Mediana Magazine</div>
          <div className="kicker mt-4">Propuesta comercial · {q.quoteNumber}</div>
          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground font-mono">
            <span>Emitida: {DATE(q.createdAt)}</span>
            {q.validUntil && <span>Válida hasta: {DATE(q.validUntil)}</span>}
            <span>Para: {q.clientName}</span>
          </div>
          <div className="mt-6">
            <Button variant="outline" asChild data-testid="public-quote-download-pdf">
              <a href={`${API}/quotes/${token}/pdf`} target="_blank" rel="noreferrer"><Download className="mr-1.5 h-4 w-4" /> Descargar PDF</a>
            </Button>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-4xl px-6 py-10 md:px-10 md:py-14 space-y-10" data-testid="public-quote-body">
        {/* status banner */}
        {q.status === "ACCEPTED" && (
          <div className="rounded-md border border-mint bg-mint/60 px-4 py-3 text-sm flex items-start gap-3" data-testid="public-quote-accepted-banner">
            <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">Cotización aceptada</div>
              <div className="text-xs text-muted-foreground mt-0.5">Aceptada por {q.acceptedByName} el {DATE(q.acceptedAt)}. Gracias por confiar en Mediana.</div>
            </div>
          </div>
        )}
        {q.status === "DECLINED" && (
          <div className="rounded-md border border-blush bg-blush/50 px-4 py-3 text-sm" data-testid="public-quote-declined-banner">
            Esta cotización fue rechazada el {DATE(q.declinedAt)}.
          </div>
        )}

        {/* parties */}
        <section className="grid gap-6 md:grid-cols-2">
          <div>
            <div className="kicker">De</div>
            <div className="mt-2 font-display text-2xl">{org.name}</div>
            <div className="mt-1 text-xs text-muted-foreground">{org.legalName}</div>
            <div className="text-xs text-muted-foreground">{org.address}</div>
            <div className="text-xs text-muted-foreground font-mono">{org.website}</div>
          </div>
          <div>
            <div className="kicker">Para</div>
            <div className="mt-2 font-display text-2xl">{q.clientName}</div>
            {data.customer?.agency && <div className="mt-1 text-xs text-muted-foreground">Agencia: {data.customer.agency}</div>}
            {q.contactName && <div className="text-xs text-muted-foreground">Contacto: {q.contactName}</div>}
            {q.contactEmail && <div className="text-xs text-muted-foreground font-mono">{q.contactEmail}</div>}
          </div>
        </section>

        {/* items */}
        <section>
          <div className="kicker mb-3">Detalle de la propuesta</div>
          <div className="rounded-md border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-2">
                <tr>
                  <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider">Concepto</th>
                  <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider w-20">Cant.</th>
                  <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider w-28">Precio</th>
                  <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider w-28">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {(q.items || []).map((it, i) => (
                  <tr key={i} className="border-t border-border align-top" data-testid="public-quote-item">
                    <td className="px-4 py-3 text-sm">
                      <div className="whitespace-pre-wrap">{it.description}</div>
                      {it.discount > 0 && <div className="mt-1 text-[11px] text-muted-foreground">Descuento aplicado: −{USD2(it.discount)}</div>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{it.qty}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{USD2(it.unitPrice)}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{USD2(it.lineTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {q.schedule && <div className="mt-3 text-xs text-muted-foreground">Calendario: {q.schedule}</div>}
        </section>

        {/* totals */}
        <section className="flex justify-end">
          <div className="w-full max-w-sm space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{USD2(q.subtotal)}</span></div>
            {q.discountTotal > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Descuentos</span><span className="font-mono">−{USD2(q.discountTotal)}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">IVA ({Math.round((q.taxRate || 0) * 100)}%)</span><span className="font-mono">{USD2(q.taxTotal)}</span></div>
            <Separator className="my-2" />
            <div className="flex items-end justify-between">
              <span className="kicker">Total {q.currency}</span>
              <span className="font-display text-4xl leading-none" data-testid="public-quote-total">{USD2(q.total)}</span>
            </div>
          </div>
        </section>

        {q.terms && (
          <section>
            <div className="kicker mb-2">Términos y condiciones</div>
            <div className="whitespace-pre-wrap rounded-md border border-border bg-surface-2 p-4 font-mono text-xs leading-relaxed">{q.terms}</div>
          </section>
        )}

        {/* actions */}
        {!closed && (
          <section className="rounded-md border border-border bg-card p-5">
            <div className="font-display text-2xl">¿Aceptas esta propuesta?</div>
            <p className="mt-1 text-sm text-muted-foreground">Al aceptar, tu nombre, tu firma, la fecha y hora, la dirección IP y el navegador quedan registrados en un acta de aceptación.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={() => setAcceptOpen(true)} data-testid="public-quote-accept-button"><Check className="mr-1.5 h-4 w-4" /> Aceptar propuesta</Button>
              <Button variant="outline" onClick={() => setDeclineOpen(true)} data-testid="public-quote-decline-button"><X className="mr-1.5 h-4 w-4" /> Rechazar</Button>
            </div>
          </section>
        )}

        <footer className="pt-6 text-center text-xs text-muted-foreground font-mono border-t border-border">
          Mediana Magazine · Propuesta generada por Mediana Hub
        </footer>
      </main>

      {/* Accept dialog */}
      <Dialog open={acceptOpen} onOpenChange={setAcceptOpen}>
        <DialogContent className="max-w-lg bg-card" data-testid="public-accept-dialog">
          <DialogHeader>
            <div className="kicker">Aceptación de propuesta</div>
            <DialogTitle className="font-display text-3xl font-normal">Firma la aceptación</DialogTitle>
            <DialogDescription>Esta firma quedará como constancia junto con el detalle de la propuesta.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid gap-1.5"><Label className="text-xs">Nombre completo</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="public-accept-name" /></div>
            <SignaturePad value={sig} onChange={setSig} label="Firma del cliente" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setAcceptOpen(false)}>Cancelar</Button>
            <Button onClick={accept} disabled={acting} data-testid="public-accept-confirm">
              {acting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Check className="mr-1.5 h-4 w-4" />}
              Confirmar aceptación
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Decline dialog */}
      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent className="max-w-md bg-card" data-testid="public-decline-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-normal">Rechazar propuesta</DialogTitle>
            <DialogDescription>Opcional: cuéntanos la razón.</DialogDescription>
          </DialogHeader>
          <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Razón del rechazo (opcional)" data-testid="public-decline-reason" />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDeclineOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={decline} disabled={acting} data-testid="public-decline-confirm">Rechazar propuesta</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
